import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CircularProgress,
  Typography,
  useMediaQuery,
  Divider,
  Button,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import useResultService from "../../api/services/ResultsService";
import generateStudentTermResultsReport from "../../utils/StudentTermResultsReport";
import PrintIcon from "@mui/icons-material/Print";

const StudentReportResults = () => {
  const { classId, termId, studentId } = useParams();
  const { getResultsByTermAndClass } = useResultService();

  const [studentResult, setStudentResult] = useState(null);
  const [classResults, setClassResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalStudents, setTotalStudents] = useState(0);

  const isSmallScreen = useMediaQuery("(max-width:600px)");

  const hasAllZeroValues = (subjectDetails) => {
    return (
      (subjectDetails.exam10Average === 0 || subjectDetails.exam10Average === null) &&
      (subjectDetails.exam40Average === 0 || subjectDetails.exam40Average === null) &&
      (subjectDetails.exam50Marks === 0 || subjectDetails.exam50Marks === null) &&
      (subjectDetails.totalMarks === 0 || subjectDetails.totalMarks === null)
    );
  };

  const getFilteredSubjects = () => {
    if (!studentResult?.subjects) return [];
    return Object.entries(studentResult.subjects).filter(
      ([, details]) => !hasAllZeroValues(details)
    );
  };

  const formatGPA = (gpa) => {
    if (gpa === null || gpa === undefined || gpa === "") return "N/A";
    return parseFloat(gpa).toFixed(1);
  };

  // Helper functions for grade points and division calculation
  const getGradePoint = (grade, classType) => {
    if (classType === "A-LEVEL") {
      const gradePoints = { A: 1, B: 2, C: 3, D: 4, E: 5, S: 6, F: 7 };
      return gradePoints[grade] || 0;
    } else {
      switch (grade) {
        case "A":
          return 1;
        case "B":
          return 2;
        case "C":
          return 3;
        case "D":
          return 4;
        case "F":
          return 5;
        default:
          return 0;
      }
    }
  };

  const getDivisionLabel = (points, classType) => {
    if (classType === "A-LEVEL") {
      if (points >= 1 && points <= 9) return `Division 1 • ${parseFloat(points).toFixed(1).replace(/\.0$/, '')}`;
      if (points >= 10 && points <= 12) return `Division 2 • ${parseFloat(points).toFixed(1).replace(/\.0$/, '')}`;
      if (points >= 13 && points <= 17) return `Division 3 • ${parseFloat(points).toFixed(1).replace(/\.0$/, '')}`;
      if (points >= 18 && points <= 21) return `Division 4 • ${parseFloat(points).toFixed(1).replace(/\.0$/, '')}`;
      return `Division 0 • ${parseFloat(points).toFixed(1).replace(/\.0$/, '')}`;
    } else {
      if (points >= 1 && points <= 17) return `Division 1 • ${points}`;
      if (points <= 21) return `Division 2 • ${points}`;
      if (points <= 25) return `Division 3 • ${points}`;
      if (points <= 33) return `Division 4 • ${points}`;
      return `Division 0 • ${points}`;
    }
  };

  const calculateDivision = () => {
    if (!studentResult?.subjects || !studentResult.classType) return "N/A";

    const aLevelSubjectsToConsider = [
      "ADVANCED MATHEMATICS",
      "ENGLISH LANGUAGE",
      "PHYSICS",
      "CHEMISTRY",
      "BIOLOGY",
      "HISTORY",
      "ECONOMICS",
      "GEOGRAPHY",
    ];

    let totalPoints = 0;
    let subjectCount = 0;

    if (studentResult.classType === "A-LEVEL") {
      // A-LEVEL: only count specific subjects
      Object.entries(studentResult.subjects).forEach(([subject, details]) => {
        const subjectUpper = subject.toUpperCase();
        
        if (!aLevelSubjectsToConsider.includes(subjectUpper)) {
          return;
        }

        if (!hasAllZeroValues(details) && details.grade) {
          const gradePoint = getGradePoint(details.grade, studentResult.classType);
          totalPoints += gradePoint;
          subjectCount++;
        }
      });
    } else {
      // O-LEVEL: get all subjects with valid grades, then take top 7
      const subjectsWithGrades = [];
      
      Object.entries(studentResult.subjects).forEach(([subject, details]) => {
        if (!hasAllZeroValues(details) && details.grade) {
          const gradePoint = getGradePoint(details.grade, studentResult.classType);
          if (gradePoint > 0) {
            subjectsWithGrades.push({
              subject,
              grade: details.grade,
              points: gradePoint
            });
          }
        }
      });

      // Sort by grade points (ascending - A=1 is best, F=5 is worst)
      subjectsWithGrades.sort((a, b) => a.points - b.points);

      // Take only the top 7 subjects (best grades)
      const top7Subjects = subjectsWithGrades.slice(0, 7);
      
      top7Subjects.forEach(subj => {
        totalPoints += subj.points;
        subjectCount++;
      });

      console.log(`O-LEVEL Division calculation for ${studentResult.studentName}:`, {
        allSubjects: subjectsWithGrades,
        top7Subjects: top7Subjects,
        totalPoints,
        subjectCount
      });
    }

    if (subjectCount === 0) return "N/A";

    return getDivisionLabel(totalPoints, studentResult.classType);
  };

  const getActiveStudentCount = (results = []) =>
    results.filter((student) => student?.studentStatus === "ACTIVE").length;

  const getTotalStudentsCount = (studentData, results = []) =>
    studentData?.totalStudents ?? getActiveStudentCount(results);

  const handlePrint = () => {
    if (!studentResult) return;
    const activeTotal = getTotalStudentsCount(studentResult, classResults);
    const blob = generateStudentTermResultsReport(
      studentResult,
      activeTotal,
      calculateDivision(),
      { returnBlob: true }
    );
    if (!blob || !(blob instanceof Blob)) return;
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (!printWindow) {
      URL.revokeObjectURL(url);
      return;
    }
    printWindow.onload = () => {
      try {
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          URL.revokeObjectURL(url);
        }, 250);
      } catch {
        URL.revokeObjectURL(url);
      }
    };
    printWindow.onerror = () => URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!classId || !termId || !studentId) {
      setLoading(false);
      return;
    }

    const loadResults = async () => {
      try {
        const response = await getResultsByTermAndClass(termId, classId);
        if (response?.data) {
          const results = response.data;
          const studentData = results.find((s) => s.studentId === studentId);
          setClassResults(results);
          setStudentResult(studentData || null);
          setTotalStudents(getTotalStudentsCount(studentData, results));
        }
      } catch (e) {
        setStudentResult(null);
      } finally {
        setLoading(false);
      }
    };

    loadResults();
  }, [classId, termId, studentId]);

  const columns = [
    { field: "subject", headerName: "Subject", flex: 1, minWidth: 100 },
    { field: "exam10Average", headerName: "Exercise(10)", flex: 1, minWidth: 50 },
    { field: "exam40Average", headerName: "Tests(40)", flex: 1, minWidth: 50 },
    { field: "exam50Marks", headerName: "Exam(50)", flex: 1, minWidth: 50 },
    { field: "totalMarks", headerName: "Total(100)", flex: 1, minWidth: 50 },
    { field: "grade", headerName: "Grade", flex: 1, minWidth: 50 },
  ];

  return (
    <Box m={2}>
      {loading ? (
        <Box display="flex" justifyContent="center" mt={8}>
          <CircularProgress />
        </Box>
      ) : studentResult ? (
        <>
          {/* ✅ Header Card */}
          <Card sx={{ p: 2, borderLeft: "6px solid green", mb: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems={isSmallScreen ? "stretch" : "center"}
              flexDirection={isSmallScreen ? "column" : "row"}
              gap={2}
              mb={2}
            >
              <Typography
                variant="h6"
                color="green"
                fontWeight="bold"
                textAlign={isSmallScreen ? "center" : "left"}
              >
                Provisional Results:
              </Typography>

              {/* ✅ Responsive Buttons */}
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ width: isSmallScreen ? "100%" : "auto" }}>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  sx={{
                    backgroundColor: "#2980b9",
                    "&:hover": { backgroundColor: "#1c5a85" },
                    py: 1.2,
                    flex: isSmallScreen ? 1 : "none",
                  }}
                >
                  Print
                </Button>
                <Button
                  fullWidth={isSmallScreen}
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    generateStudentTermResultsReport(
                      studentResult,
                      getTotalStudentsCount(studentResult, classResults),
                      calculateDivision()
                    )
                  }
                  sx={{
                    backgroundColor: "#502eccff",
                    "&:hover": {
                      backgroundColor: "#3d2399",
                    },
                    py: 1.2,
                    flex: isSmallScreen ? 1 : "none",
                  }}
                >
                  📄 Download Report
                </Button>
              </Box>
            </Box>

            <Box display="flex" gap={4} flexWrap="wrap">
              <Box>
                <Typography fontWeight="bold">Seminarian Name</Typography>
                <Typography>{studentResult.studentName}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Class</Typography>
                <Typography>{studentResult.className}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Class Type</Typography>
                <Typography>{studentResult.classType || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Teacher-Phone</Typography>
                <Typography>{studentResult.classTeacherPhoneNumber || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Class-Teacher</Typography>
                <Typography>{studentResult.classTeacher || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Term Name</Typography>
                <Typography>{studentResult.termName || "N/A"}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Combination</Typography>
                <Typography>{studentResult.combination}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Total Marks</Typography>
                <Typography>{studentResult.totalMarks}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Average</Typography>
                <Typography>{studentResult.average}</Typography>
              </Box>
              <Box>
                <Typography fontWeight="bold">Division</Typography>
                <Typography>{calculateDivision()}</Typography>
              </Box>
              {/* <Box>
                <Typography fontWeight="bold">GPA</Typography>
                <Typography>{formatGPA(studentResult.gpa)}</Typography>
              </Box> */}
              <Box>
                <Typography fontWeight="bold">Position</Typography>
                <Typography>
                  {studentResult.positionText} out of {totalStudents}
                </Typography>
              </Box>
            </Box>
          </Card>

          {/* ✅ Responsive Results Section */}
          {isSmallScreen ? (
            <Box display="flex" flexDirection="column" gap={2}>
              {getFilteredSubjects().map(([subject, details], idx) => (
                <Card key={idx} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {subject}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography>Exam10 Avg: {details.exam10Average}</Typography>
                  <Typography>Exam40 Avg: {details.exam40Average}</Typography>
                  <Typography>Exam50 Marks: {details.exam50Marks}</Typography>
                  <Typography>Total: {details.totalMarks}</Typography>
                  <Typography>Grade: {details.grade}</Typography>
                </Card>
              ))}
            </Box>
          ) : (
            <Box height={400}>
              <DataGrid
                rows={getFilteredSubjects().map(([subject, details], idx) => ({
                  id: idx,
                  subject,
                  ...details,
                }))}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[10]}
                disableRowSelectionOnClick
                rowHeight={35}
                headerHeight={40}
              />
            </Box>
          )}
        </>
      ) : (
        <Typography textAlign="center" mt={4} color="text.secondary">
          No results found.
        </Typography>
      )}
    </Box>
  );
};

export default StudentReportResults;
